import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Grid, 
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Badge,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { useAuth } from '../contexts/AuthContext';
import { getAdminStats, getPendingPhysicians, getPendingSponsors } from '../services/adminService';
import PendingApprovalsTab from './admin/PendingApprovalsTab';
import ClinicManagement from './admin/ClinicManagement';
import PatientManagement from './admin/PatientManagement';
import PhysicianManagement from './admin/PhysicianManagement';
import SponsorManagement from './admin/SponsorManagement';
import ClinicStaffManagement from './admin/ClinicStaffManagement';
import UserRoleManagement from './admin/UserRoleManagement';
import ClinicSelector from './shared/ClinicSelector';
import ViewModeBanner from './shared/ViewModeBanner';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArticleIcon from '@mui/icons-material/Article';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import BusinessIcon from '@mui/icons-material/Business';

// Animation
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components
const AdminContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(3, 0),
  animation: `${fadeIn} 0.5s ease-out`,
}));

const AdminHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  background: 'linear-gradient(to right, rgba(255,255,255,0.9), rgba(240,248,255,0.9))',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
  flexWrap: 'wrap',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2, 3),
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    marginBottom: theme.spacing(3),
    flexWrap: 'nowrap',
  }
}));

const StatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  width: '100%',
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  border: 'none',
  overflow: 'hidden',
  position: 'relative',
  margin: 0,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
  }
}));

const StatsCardContent = styled(CardContent)(({ theme, color }) => ({
  padding: theme.spacing(2),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    backgroundColor: color || theme.palette.primary.main,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
    marginBottom: theme.spacing(0.25),
  },
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

const StatSubLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  opacity: 0.8,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
  },
}));

const AdminBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.success.light,
  color: theme.palette.success.dark,
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 2px 8px rgba(0,150,136,0.2)',
  border: 'none',
  '& .MuiSvgIcon-root': {
    filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))'
  }
}));

const TabContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  border: 'none',
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(3),
}));

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 0, sm: 0, md: 0 } }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Mock data for users
// Mock data removed - now using real data from adminService

const Admin = () => {
  const { profile, tenant } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Real data state management
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalPhysicians: 0,
    totalPatients: 0,
    pendingApprovals: 0,
  });
  const [pendingCounts, setPendingCounts] = useState({
    physicians: 0,
    sponsors: 0,
    total: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  
  // Load real data from admin service
  useEffect(() => {
    async function loadStats() {
      setIsLoading(true);
      try {
        const [statsData, physiciansResult, sponsorsResult] = await Promise.all([
          getAdminStats(),
          getPendingPhysicians(),
          getPendingSponsors()
        ]);
        
        setStats(statsData);
        
        // Get separate counts for badges
        const physicianCount = physiciansResult.data?.length || 0;
        const sponsorCount = sponsorsResult.data?.length || 0;
        
        setPendingCounts({
          physicians: physicianCount,
          sponsors: sponsorCount,
          total: physicianCount + sponsorCount
        });
      } catch (error) {
        console.error('Failed to load admin stats:', error);
      } finally {
        setIsLoading(false);
      }
    }
    loadStats();
  }, []);
  
  // Refresh pending counts when tab changes to Pending Approvals (in case approvals were processed)
  useEffect(() => {
    if (activeTab === 1) {
      async function refreshPendingCounts() {
        try {
          const [physiciansResult, sponsorsResult] = await Promise.all([
            getPendingPhysicians(),
            getPendingSponsors()
          ]);
          
          const physicianCount = physiciansResult.data?.length || 0;
          const sponsorCount = sponsorsResult.data?.length || 0;
          
          setPendingCounts({
            physicians: physicianCount,
            sponsors: sponsorCount,
            total: physicianCount + sponsorCount
          });
          
          // Also update stats
          const statsData = await getAdminStats();
          setStats(statsData);
        } catch (error) {
          console.error('Failed to refresh pending counts:', error);
        }
      }
      refreshPendingCounts();
    }
  }, [activeTab]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  
  // Delete dialog handlers removed - handled by specialized components
  
  // Note: User and appointment filtering will be implemented when backend APIs are ready
  // For now, displaying real stats from the database
  
  return (
    <AdminContainer>
      <ViewModeBanner />
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
        <AdminHeader>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700, 
                color: 'text.primary',
                fontSize: { xs: '1.5rem', sm: '2.125rem' }
              }}
            >
              Welcome, Dr. {profile?.first_name || 'Admin'}!
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                {tenant?.name || 'Alpha01 Telemedicine'}
              </Typography>
              <ClinicSelector />
            </Box>
          </Box>
          
          {/* Admin Status Badge */}
          <AdminBadge sx={{ mt: { xs: 1, sm: 0 }, ml: { xs: 0, sm: 'auto' } }}>
            <AdminPanelSettingsIcon sx={{ mr: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '1rem' } }}>
              Admin Mode Active
            </Typography>
          </AdminBadge>
        </AdminHeader>
        
        {/* Stats Cards */}
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            gap: 2,
            mb: 3, 
            px: { xs: 2, sm: 3 },
            width: '100%',
          }}
        >
          <Box sx={{ 
            flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' },
            minWidth: 0,
          }}>
            <StatsCard>
              <StatsCardContent color="primary.main">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Box
                      sx={{
                        width: { xs: 28, sm: 36 },
                        height: { xs: 28, sm: 36 },
                        borderRadius: '50%',
                        bgcolor: 'primary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: { xs: 1, sm: 2 },
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        flexShrink: 0,
                      }}
                    >
                      <PeopleIcon color="primary" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />
                    </Box>
                    <Box>
                      <StatLabel>Users</StatLabel>
                      <StatSubLabel sx={{ mt: 0.25 }}>{isLoading ? 'Loading...' : `${stats.activeUsers} active`}</StatSubLabel>
                    </Box>
                  </Box>
                  <StatValue sx={{ ml: 2 }}>{isLoading ? '...' : stats.totalUsers}</StatValue>
                </Box>
              </StatsCardContent>
            </StatsCard>
          </Box>
          
          <Box sx={{ 
            flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' },
            minWidth: 0,
          }}>
            <StatsCard>
              <StatsCardContent color="secondary.main">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Box
                      sx={{
                        width: { xs: 28, sm: 36 },
                        height: { xs: 28, sm: 36 },
                        borderRadius: '50%',
                        bgcolor: 'secondary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: { xs: 1, sm: 2 },
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        flexShrink: 0,
                      }}
                    >
                      <EventIcon color="secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />
                    </Box>
                    <Box>
                      <StatLabel>Appointments</StatLabel>
                      <StatSubLabel sx={{ mt: 0.25 }}>Upcoming</StatSubLabel>
                    </Box>
                  </Box>
                  <StatValue sx={{ ml: 2 }}>{isLoading ? '...' : stats.upcomingAppointments || 0}</StatValue>
                </Box>
              </StatsCardContent>
            </StatsCard>
          </Box>
          
          <Box sx={{ 
            flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' },
            minWidth: 0,
          }}>
            <StatsCard>
              <StatsCardContent color="success.main">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Box
                      sx={{
                        width: { xs: 28, sm: 36 },
                        height: { xs: 28, sm: 36 },
                        borderRadius: '50%',
                        bgcolor: 'success.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: { xs: 1, sm: 2 },
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        flexShrink: 0,
                      }}
                    >
                      <LocalHospitalIcon color="success" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />
                    </Box>
                    <Box>
                      <StatLabel>Doctors</StatLabel>
                      <StatSubLabel sx={{ mt: 0.25 }}>Active providers</StatSubLabel>
                    </Box>
                  </Box>
                  <StatValue sx={{ ml: 2 }}>{isLoading ? '...' : stats.totalPhysicians}</StatValue>
                </Box>
              </StatsCardContent>
            </StatsCard>
          </Box>
          
          <Box sx={{ 
            flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' },
            minWidth: 0,
          }}>
            <StatsCard>
              <StatsCardContent color="info.main">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Box
                      sx={{
                        width: { xs: 28, sm: 36 },
                        height: { xs: 28, sm: 36 },
                        borderRadius: '50%',
                        bgcolor: 'info.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: { xs: 1, sm: 2 },
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        flexShrink: 0,
                      }}
                    >
                      <PersonIcon color="info" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />
                    </Box>
                    <Box>
                      <StatLabel>Patients</StatLabel>
                      <StatSubLabel sx={{ mt: 0.25 }}>Registered</StatSubLabel>
                    </Box>
                  </Box>
                  <StatValue sx={{ ml: 2 }}>{isLoading ? '...' : stats.totalPatients}</StatValue>
                </Box>
              </StatsCardContent>
            </StatsCard>
          </Box>
        </Box>
        
        {/* Recent Activity - Coming Soon */}
        <Box 
          component={Paper}
          elevation={3}
          sx={{ 
            borderRadius: 4,
            mb: 3,
            overflow: 'hidden',
            bgcolor: 'rgba(178, 245, 234, 0.1)',
            mx: { xs: 1, sm: 3 },
          }}
        >
          <Box sx={{ p: { xs: 2.5, sm: 4 }, textAlign: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: 'text.primary', 
                mb: 1.5,
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              Activity Logging
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary', 
                mb: 1.5,
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              Activity monitoring and logging features will be available in an upcoming release.
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              Track user actions, system events, and administrative activities in real-time.
            </Typography>
          </Box>
        </Box>
        
        {/* Main Tabs */}
        <TabContainer sx={{ mx: { xs: 1, sm: 3 } }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="admin tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              bgcolor: 'background.paper',
              '& .MuiTab-root': {
                minHeight: { xs: 56, sm: 64 },
                py: 1,
                px: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: { xs: 90, sm: 120 },
              },
              '& .MuiTabs-scrollButtons': {
                width: { xs: 36, sm: 40 },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1.2rem', sm: '1.5rem' },
              },
            }}
          >
            <Tab 
              icon={<BusinessIcon />} 
              label="Clinic Management" 
              id="admin-tab-0"
              aria-controls="admin-tabpanel-0"
              sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
                '& .MuiSvgIcon-root': { mr: 1 },
                bgcolor: activeTab === 0 ? 'primary.light' : 'transparent',
                color: activeTab === 0 ? 'primary.dark' : 'inherit',
                fontWeight: activeTab === 0 ? 700 : 400,
              }}
            />
            <Tab 
              icon={
                <Badge 
                  badgeContent={pendingCounts.total} 
                  color="error"
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.7rem',
                      height: '18px',
                      minWidth: '18px',
                      padding: '0 4px'
                    }
                  }}
                >
                  <PendingActionsIcon />
                </Badge>
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Pending Approvals</span>
                  {pendingCounts.total > 0 && (
                    <Chip 
                      label={pendingCounts.total} 
                      size="small" 
                      color="error"
                      sx={{ height: '20px', fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              }
              id="admin-tab-1"
              aria-controls="admin-tabpanel-1"
              sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
                '& .MuiSvgIcon-root': { mr: 1 },
                bgcolor: activeTab === 1 ? 'warning.light' : 'transparent',
                color: activeTab === 1 ? 'warning.dark' : 'inherit',
                fontWeight: activeTab === 1 ? 700 : 400,
              }}
            />
            <Tab 
              icon={<PeopleIcon />} 
              label="Patient Management" 
              id="admin-tab-2"
              aria-controls="admin-tabpanel-2"
              sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
                '& .MuiSvgIcon-root': { mr: 1 }
              }}
            />
            <Tab 
              icon={
                pendingCounts.physicians > 0 ? (
                  <Badge 
                    badgeContent={pendingCounts.physicians} 
                    color="error"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        height: '18px',
                        minWidth: '18px',
                        padding: '0 4px'
                      }
                    }}
                  >
                    <LocalHospitalIcon />
                  </Badge>
                ) : (
                  <LocalHospitalIcon />
                )
              }
              label="Physician Management" 
              id="admin-tab-3"
              aria-controls="admin-tabpanel-3"
              sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
                '& .MuiSvgIcon-root': { mr: 1 }
              }}
            />
            <Tab 
              icon={
                pendingCounts.sponsors > 0 ? (
                  <Badge 
                    badgeContent={pendingCounts.sponsors} 
                    color="error"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        height: '18px',
                        minWidth: '18px',
                        padding: '0 4px'
                      }
                    }}
                  >
                    <PersonIcon />
                  </Badge>
                ) : (
                  <PersonIcon />
                )
              }
              label="Sponsor Management" 
              id="admin-tab-4"
              aria-controls="admin-tabpanel-4"
              sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
                '& .MuiSvgIcon-root': { mr: 1 }
              }}
            />
            <Tab 
              icon={<PeopleIcon />} 
              label="Clinic Staff Management" 
              id="admin-tab-5"
              aria-controls="admin-tabpanel-5"
              sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
                '& .MuiSvgIcon-root': { mr: 1 }
              }}
            />
            <Tab 
              icon={<PersonIcon />} 
              label="Users" 
              id="admin-tab-6"
              aria-controls="admin-tabpanel-6"
              sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
                '& .MuiSvgIcon-root': { mr: 1 }
              }}
            />
            <Tab 
              icon={<CalendarMonthIcon />} 
              label="Appointments" 
              id="admin-tab-7"
              aria-controls="admin-tabpanel-7"
              sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
                '& .MuiSvgIcon-root': { mr: 1 }
              }}
            />
            <Tab 
              icon={<ArticleIcon />} 
              label="Content" 
              id="admin-tab-8"
              aria-controls="admin-tabpanel-8"
              sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
                '& .MuiSvgIcon-root': { mr: 1 }
              }}
            />
            <Tab 
              icon={<SettingsIcon />} 
              label="Settings" 
              id="admin-tab-9"
              aria-controls="admin-tabpanel-9"
              sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
                '& .MuiSvgIcon-root': { mr: 1 }
              }}
            />
          </Tabs>
          
          {/* Search and Filter Bar */}
          <Box 
            sx={{ 
              p: { xs: 1.5, sm: 2 },
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 1.5, sm: 0 },
              justifyContent: 'space-between',
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <TextField
              placeholder="Search..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearch}
              sx={{ 
                width: { xs: '100%', sm: 300 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 4,
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
              {/* Removed redundant Add buttons - clinic staff and patients are created at tenant clinic side */}
              <IconButton 
                size="small"
                sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          
          {/* Clinic Management Tab Panel */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'info.light' }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  📋 Desktop Required
                </Typography>
                <Typography variant="body2">
                  Clinic management features require a desktop or tablet for the best experience.
                  Please use a larger screen for full management capabilities.
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <ClinicManagement />
            </Box>
          </TabPanel>
          
          {/* Pending Approvals Tab Panel */}
          <TabPanel value={activeTab} index={1}>
            <PendingApprovalsTab 
              onApprovalProcessed={() => {
                // Refresh pending counts when approval is processed
                async function refreshCounts() {
                  try {
                    const [physiciansResult, sponsorsResult] = await Promise.all([
                      getPendingPhysicians(),
                      getPendingSponsors()
                    ]);
                    
                    const physicianCount = physiciansResult.data?.length || 0;
                    const sponsorCount = sponsorsResult.data?.length || 0;
                    
                    setPendingCounts({
                      physicians: physicianCount,
                      sponsors: sponsorCount,
                      total: physicianCount + sponsorCount
                    });
                    
                    // Also update stats
                    const statsData = await getAdminStats();
                    setStats(statsData);
                  } catch (error) {
                    console.error('Failed to refresh pending counts:', error);
                  }
                }
                refreshCounts();
              }}
            />
          </TabPanel>
          
          {/* Patient Management Tab Panel */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'info.light' }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  📋 Desktop Required
                </Typography>
                <Typography variant="body2">
                  Patient management features require a desktop or tablet for the best experience.
                  Please use a larger screen for full management capabilities.
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <PatientManagement />
            </Box>
          </TabPanel>
          
          {/* Physician Management Tab Panel */}
          <TabPanel value={activeTab} index={3}>
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'info.light' }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  📋 Desktop Required
                </Typography>
                <Typography variant="body2">
                  Physician management features require a desktop or tablet for the best experience.
                  Please use a larger screen for full management capabilities.
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <PhysicianManagement />
            </Box>
          </TabPanel>
          
          {/* Sponsor Management Tab Panel */}
          <TabPanel value={activeTab} index={4}>
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'info.light' }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  📋 Desktop Required
                </Typography>
                <Typography variant="body2">
                  Sponsor management features require a desktop or tablet for the best experience.
                  Please use a larger screen for full management capabilities.
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <SponsorManagement />
            </Box>
          </TabPanel>
          
          {/* Clinic Staff Management Tab Panel */}
          <TabPanel value={activeTab} index={5}>
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'info.light' }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  📋 Desktop Required
                </Typography>
                <Typography variant="body2">
                  Clinic staff management features require a desktop or tablet for the best experience.
                  Please use a larger screen for full management capabilities.
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <ClinicStaffManagement />
            </Box>
          </TabPanel>
          
          {/* Users Tab Panel */}
          <TabPanel value={activeTab} index={6}>
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'info.light' }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  📋 Desktop Required
                </Typography>
                <Typography variant="body2">
                  User role management features require a desktop or tablet for the best experience.
                  Please use a larger screen for full management capabilities.
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <UserRoleManagement />
            </Box>
          </TabPanel>
          
          {/* Appointments Tab Panel */}
          <TabPanel value={activeTab} index={7}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient</TableCell>
                    <TableCell>Doctor</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                        Appointment management will be available in an upcoming release.
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Comprehensive appointment scheduling and management features will be available once the backend appointment system is fully implemented.
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TabPanel>
          
          {/* Content Tab Panel */}
          <TabPanel value={activeTab} index={8}>
            <Box sx={{ px: 2 }}>
              {/* FAQ Management - Full Width */}
              <Paper elevation={0} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2, mb: 1.5 }}>
                <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
                  FAQ Management
                </Typography>
                <List sx={{ py: 0 }}>
                  <ListItem sx={{ py: 1 }} secondaryAction={<IconButton edge="end" size="small"><EditIcon fontSize="small" /></IconButton>}>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>How do I schedule an appointment?</Typography>}
                      secondary={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Last updated: 2025-06-01</Typography>}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem sx={{ py: 1 }} secondaryAction={<IconButton edge="end" size="small"><EditIcon fontSize="small" /></IconButton>}>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>How do video consultations work?</Typography>}
                      secondary={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Last updated: 2025-06-05</Typography>}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem sx={{ py: 1 }} secondaryAction={<IconButton edge="end" size="small"><EditIcon fontSize="small" /></IconButton>}>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>What should I do if I need to cancel or reschedule?</Typography>}
                      secondary={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Last updated: 2025-05-20</Typography>}
                    />
                  </ListItem>
                </List>
                <Button variant="outlined" fullWidth startIcon={<AddIcon />} size="small" sx={{ mt: 1.5 }}>Add New FAQ</Button>
              </Paper>

              {/* System Notifications - Full Width */}
              <Paper elevation={0} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2, mb: 1.5 }}>
                <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
                  System Notifications
                </Typography>
                <List sx={{ py: 0 }}>
                  <ListItem sx={{ py: 1 }} secondaryAction={<IconButton edge="end" size="small"><EditIcon fontSize="small" /></IconButton>}>
                    <ListItemIcon sx={{ minWidth: 36 }}><CheckCircleIcon color="success" sx={{ fontSize: '1.25rem' }} /></ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>System maintenance completed</Typography>}
                      secondary={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Published: 2025-06-10</Typography>}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem sx={{ py: 1 }} secondaryAction={<IconButton edge="end" size="small"><EditIcon fontSize="small" /></IconButton>}>
                    <ListItemIcon sx={{ minWidth: 36 }}><WarningIcon color="warning" sx={{ fontSize: '1.25rem' }} /></ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>Scheduled maintenance on June 15</Typography>}
                      secondary={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Published: 2025-06-08</Typography>}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem sx={{ py: 1 }} secondaryAction={<IconButton edge="end" size="small"><EditIcon fontSize="small" /></IconButton>}>
                    <ListItemIcon sx={{ minWidth: 36 }}><ErrorIcon color="error" sx={{ fontSize: '1.25rem' }} /></ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>Important update to privacy policy</Typography>}
                      secondary={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Published: 2025-06-01</Typography>}
                    />
                  </ListItem>
                </List>
                <Button variant="outlined" fullWidth startIcon={<AddIcon />} size="small" sx={{ mt: 1.5 }}>Add New Notification</Button>
              </Paper>

              {/* Health Resources - Full Width */}
              <Paper elevation={0} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
                  Health Resources
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Published Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>COVID-19 Vaccination Information</TableCell>
                        <TableCell>Preventive Care</TableCell>
                        <TableCell>2025-05-15</TableCell>
                        <TableCell><Chip label="Published" color="success" size="small" /></TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="primary"><VisibilityIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="secondary"><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Diabetes Management Guide</TableCell>
                        <TableCell>Chronic Conditions</TableCell>
                        <TableCell>2025-06-01</TableCell>
                        <TableCell><Chip label="Published" color="success" size="small" /></TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="primary"><VisibilityIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="secondary"><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Mental Health Resources</TableCell>
                        <TableCell>Wellness</TableCell>
                        <TableCell>2025-06-08</TableCell>
                        <TableCell><Chip label="Draft" color="warning" size="small" /></TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="primary"><VisibilityIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="secondary"><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button variant="outlined" fullWidth startIcon={<AddIcon />} size="small" sx={{ mt: 1.5 }}>Add New Resource</Button>
              </Paper>
            </Box>
          </TabPanel>
          
          {/* Settings Tab Panel */}
          <TabPanel value={activeTab} index={9}>
            <Box sx={{ px: 2 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 1.5, 
                    border: 1, 
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem' }}>
                    System Settings
                  </Typography>
                  <List>
                    <ListItem>
                      <FormControlLabel
                        control={<Switch checked={true} />}
                        label="Enable Video Consultations"
                      />
                    </ListItem>
                    <Divider component="li" />
                    <ListItem>
                      <FormControlLabel
                        control={<Switch checked={true} />}
                        label="Allow Patient Self-Registration"
                      />
                    </ListItem>
                    <Divider component="li" />
                    <ListItem>
                      <FormControlLabel
                        control={<Switch checked={false} />}
                        label="Require Admin Approval for New Accounts"
                      />
                    </ListItem>
                    <Divider component="li" />
                    <ListItem>
                      <FormControlLabel
                        control={<Switch checked={true} />}
                        label="Send Email Notifications"
                      />
                    </ListItem>
                    <Divider component="li" />
                    <ListItem>
                      <FormControlLabel
                        control={<Switch checked={true} />}
                        label="Send SMS Notifications"
                      />
                    </ListItem>
                  </List>
                  <Button 
                    variant="contained" 
                    color="primary"
                    fullWidth
                    size="small"
                    sx={{ mt: 1.5 }}
                  >
                    Save Settings
                  </Button>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 1.5, 
                    border: 1, 
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem' }}>
                    Working Hours
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Day</TableCell>
                          <TableCell>Open</TableCell>
                          <TableCell>Close</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Monday</TableCell>
                          <TableCell>8:00 AM</TableCell>
                          <TableCell>8:00 PM</TableCell>
                          <TableCell>
                            <Chip label="Open" color="success" size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="secondary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Tuesday</TableCell>
                          <TableCell>8:00 AM</TableCell>
                          <TableCell>8:00 PM</TableCell>
                          <TableCell>
                            <Chip label="Open" color="success" size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="secondary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Wednesday</TableCell>
                          <TableCell>8:00 AM</TableCell>
                          <TableCell>8:00 PM</TableCell>
                          <TableCell>
                            <Chip label="Open" color="success" size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="secondary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Thursday</TableCell>
                          <TableCell>8:00 AM</TableCell>
                          <TableCell>8:00 PM</TableCell>
                          <TableCell>
                            <Chip label="Open" color="success" size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="secondary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Friday</TableCell>
                          <TableCell>8:00 AM</TableCell>
                          <TableCell>8:00 PM</TableCell>
                          <TableCell>
                            <Chip label="Open" color="success" size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="secondary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Saturday</TableCell>
                          <TableCell>9:00 AM</TableCell>
                          <TableCell>5:00 PM</TableCell>
                          <TableCell>
                            <Chip label="Open" color="success" size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="secondary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Sunday</TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>
                            <Chip label="Closed" color="error" size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="secondary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button 
                    variant="contained" 
                    color="primary"
                    fullWidth
                    size="small"
                    sx={{ mt: 1.5 }}
                  >
                    Save Working Hours
                  </Button>
                </Paper>
              </Grid>
            </Grid>
            </Box>
          </TabPanel>
        </TabContainer>
      </Container>
      
      {/* Delete confirmation dialogs handled by specialized components */}
    </AdminContainer>
  );
};

export default Admin;
